import React, { useState, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  Grid,
  Button,
  Alert,
  AlertTitle,
  List,
  ListItem,
  ListItemText,
} from '@mui/material';
import { 
  parseIpcCrrCsvWithCn61Extra, 
  convertIpcCrrDataToCsv, 
  generateIpcCrrSampleCsv, 
  applyCsvDataToIpcCrr, 
  downloadIpcCrrCsv,
  IpcCn61ExtraData,
  convertCn61ExtraDataToInputFormat
} from '../../../../utils/ipcCrrCsvUtils';

interface RemunerationGroup {
  id: number;
  name: string;
  operator: string | null;
  displayName: string;
  predefined: boolean;
  homeCountry: boolean;
}

interface FormatData {
  remunerationGroup: RemunerationGroup;
  formatP: {
    tdKg: string | null;
    tdItem: string | null;
  } | null;
  formatG: {
    tdKg: string | null;
    tdItem: string | null;
  } | null;
  formatE: {
    tdKg: string | null;
    tdItem: string | null;
  } | null;
}

interface CRRCommand {
  inbound: {
    priority: FormatData[];
    nonPriority: FormatData[];
  } | null;
  outbound: {
    priority: FormatData[];
    nonPriority: FormatData[];
  } | null;
}

interface CRRData {
  flow: string;
  dto: {
    ppo: string;
    year: number;
    direction: string;
  };
  command: CRRCommand;
}

interface CRRTableProps {
  data: CRRData;
  onDataChange?: (updatedData: CRRData) => void;
  onError?: (message: string) => void;
  onSuccess?: (message: string) => void;
  currentUserPpo?: string;
  onCn61ExtraDataChange?: (cn61ExtraData: IpcCn61ExtraData[]) => void;
}

interface CRRSubTableProps {
  title: string;
  data: FormatData[];
  onDataChange: (index: number, field: 'formatP' | 'formatG' | 'formatE', subField: 'tdKg' | 'tdItem', value: string) => void;
  onBlur: (index: number, field: 'formatP' | 'formatG' | 'formatE', subField: 'tdKg' | 'tdItem', value: string) => void;
}

function CRRSubTable({ title, data, onDataChange, onBlur }: CRRSubTableProps): React.ReactElement {
  if (!data || data.length === 0) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 2 }}>
        <Typography sx={{ color: '#666', fontStyle: 'italic' }}>
          No {title.toLowerCase()} data available
        </Typography>
      </Box>
    );
  }

  // Determine if this is inbound based on title
  const isInbound = title.toLowerCase().includes('inbound');
  
  // Background colors based on direction (matching CN61Table)
  const getBackgroundColor = (isMainRow: boolean) => {
    if (isInbound) {
      return isMainRow ? '#e8f5e8' : '#f0f8f0'; // Green shades for inbound
    } else {
      return isMainRow ? '#ffe8e8' : '#fff0f0'; // Red shades for outbound
    }
  };

  const inputSx = useMemo(() => ({
    width: '100%',
    '& .MuiOutlinedInput-root': {
      height: '32px',
      fontSize: '0.875rem'
    }
  }), []);

  const inputPropsDecimal = useMemo(() => ({
    inputMode: 'decimal' as const,
    pattern: '^\\\d*(\\.\\d{0,3})?$',
    placeholder: '0.000'
  } as const), []);

  const handleBlurToThreeDecimals = useCallback((
    index: number,
    field: 'formatP' | 'formatG' | 'formatE',
    subField: 'tdKg' | 'tdItem',
    value: string
  ) => {
    // Pass the value to parent's blur handler which will format it
    onBlur(index, field, subField, value);
  }, [onBlur]);

  const formatDisplayValue = useCallback((val: string | number | null) => {
    if (val === null || val === undefined || val === '') return '';
    // Return as-is for string values (user is typing)
    return String(val);
  }, []);

  // Check if input has exactly 3 decimals (for visual validation)
  const hasValidFormat = useCallback((val: string | number | null) => {
    if (val === null || val === undefined || val === '') return false;
    const strVal = String(val);
    return /^\d+\.\d{3}$/.test(strVal);
  }, []);

  return (
    <Box sx={{ mb: 3 }}>
      <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: '#063142' }}>
        {title}
      </Typography>
      <TableContainer component={Paper} sx={{ mb: 3 }}>
        <Table size="small" sx={{ border: '1px solid #e0e0e0' }}>
          <TableHead>
            {/* Main headers */}
            <TableRow>
              <TableCell 
                rowSpan={2} 
                sx={{ 
                  fontWeight: 700, 
                  backgroundColor: '#f5f5f5', 
                  border: '1px solid #e0e0e0',
                  textAlign: 'center',
                  minWidth: 200, 
                  verticalAlign: 'middle' 
                }}
              >
                {title}
              </TableCell>
              <TableCell 
                colSpan={2} 
                sx={{ 
                  fontWeight: 700, 
                  backgroundColor: '#f5f5f5', 
                  border: '1px solid #e0e0e0',
                  textAlign: 'center' 
                }}
              >
                Format P
              </TableCell>
              <TableCell 
                colSpan={2} 
                sx={{ 
                  fontWeight: 700, 
                  backgroundColor: '#f5f5f5', 
                  border: '1px solid #e0e0e0',
                  textAlign: 'center' 
                }}
              >
                Format G
              </TableCell>
              <TableCell 
                colSpan={2} 
                sx={{ 
                  fontWeight: 700, 
                  backgroundColor: '#f5f5f5', 
                  border: '1px solid #e0e0e0',
                  textAlign: 'center' 
                }}
              >
                Format E
              </TableCell>
            </TableRow>
            {/* Sub headers */}
            <TableRow>
              <TableCell 
                sx={{ 
                  fontWeight: 700, 
                  backgroundColor: '#f5f5f5', 
                  border: '1px solid #e0e0e0',
                  textAlign: 'center',
                  width: '125px'
                }}
              >
                TD/kg
              </TableCell>
              <TableCell 
                sx={{ 
                  fontWeight: 700, 
                  backgroundColor: '#f5f5f5', 
                  border: '1px solid #e0e0e0',
                  textAlign: 'center',
                  width: '125px'
                }}
              >
                TD/item
              </TableCell>
              <TableCell 
                sx={{ 
                  fontWeight: 700, 
                  backgroundColor: '#f5f5f5', 
                  border: '1px solid #e0e0e0',
                  textAlign: 'center',
                  width: '125px'
                }}
              >
                TD/kg
              </TableCell>
              <TableCell 
                sx={{ 
                  fontWeight: 700, 
                  backgroundColor: '#f5f5f5', 
                  border: '1px solid #e0e0e0',
                  textAlign: 'center',
                  width: '125px'
                }}
              >
                TD/item
              </TableCell>
              <TableCell 
                sx={{ 
                  fontWeight: 700, 
                  backgroundColor: '#f5f5f5', 
                  border: '1px solid #e0e0e0',
                  textAlign: 'center',
                  width: '125px'
                }}
              >
                TD/kg
              </TableCell>
              <TableCell 
                sx={{ 
                  fontWeight: 700, 
                  backgroundColor: '#f5f5f5', 
                  border: '1px solid #e0e0e0',
                  textAlign: 'center',
                  width: '125px'
                }}
              >
                TD/item
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data.map((item, index) => (
              <TableRow key={`${item.remunerationGroup.id}-${index}`}>
                <TableCell 
                  sx={{ 
                    fontWeight: 600, 
                    backgroundColor: getBackgroundColor(true),
                    border: '1px solid #e0e0e0'
                  }}
                >
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {item.remunerationGroup.displayName}
                    </Typography>
                    {item.remunerationGroup.name !== item.remunerationGroup.displayName && (
                      <Typography variant="caption" sx={{ color: '#666' }}>
                        {item.remunerationGroup.name}
                      </Typography>
                    )}
                  </Box>
                </TableCell>
                {/* Format P columns */}
                <TableCell sx={{ border: '1px solid #e0e0e0' }}>
                  <TextField
                    size="small"
                    type="text"
                    value={formatDisplayValue(item.formatP?.tdKg ?? '')}
                    onChange={(e) => onDataChange(index, 'formatP', 'tdKg', e.target.value)}
                    onBlur={(e) => handleBlurToThreeDecimals(index, 'formatP', 'tdKg', e.target.value)}
                    sx={{
                      ...inputSx,
                      '& .MuiOutlinedInput-root': {
                        ...inputSx['& .MuiOutlinedInput-root'],
                        '& fieldset': {
                          borderColor: item.formatP?.tdKg && !hasValidFormat(item.formatP.tdKg) ? '#d32f2f' : undefined,
                        },
                      },
                    }}
                    inputProps={inputPropsDecimal}
                    error={item.formatP?.tdKg ? !hasValidFormat(item.formatP.tdKg) : false}
                  />
                </TableCell>
                <TableCell sx={{ border: '1px solid #e0e0e0' }}>
                  <TextField
                    size="small"
                    type="text"
                    value={formatDisplayValue(item.formatP?.tdItem ?? '')}
                    onChange={(e) => onDataChange(index, 'formatP', 'tdItem', e.target.value)}
                    onBlur={(e) => handleBlurToThreeDecimals(index, 'formatP', 'tdItem', e.target.value)}
                    sx={{
                      ...inputSx,
                      '& .MuiOutlinedInput-root': {
                        ...inputSx['& .MuiOutlinedInput-root'],
                        '& fieldset': {
                          borderColor: item.formatP?.tdItem && !hasValidFormat(item.formatP.tdItem) ? '#d32f2f' : undefined,
                        },
                      },
                    }}
                    inputProps={inputPropsDecimal}
                    error={item.formatP?.tdItem ? !hasValidFormat(item.formatP.tdItem) : false}
                  />
                </TableCell>
                {/* Format G columns */}
                <TableCell sx={{ border: '1px solid #e0e0e0' }}>
                  <TextField
                    size="small"
                    type="text"
                    value={formatDisplayValue(item.formatG?.tdKg ?? '')}
                    onChange={(e) => onDataChange(index, 'formatG', 'tdKg', e.target.value)}
                    onBlur={(e) => handleBlurToThreeDecimals(index, 'formatG', 'tdKg', e.target.value)}
                    sx={{
                      ...inputSx,
                      '& .MuiOutlinedInput-root': {
                        ...inputSx['& .MuiOutlinedInput-root'],
                        '& fieldset': {
                          borderColor: item.formatG?.tdKg && !hasValidFormat(item.formatG.tdKg) ? '#d32f2f' : undefined,
                        },
                      },
                    }}
                    inputProps={inputPropsDecimal}
                    error={item.formatG?.tdKg ? !hasValidFormat(item.formatG.tdKg) : false}
                  />
                </TableCell>
                <TableCell sx={{ border: '1px solid #e0e0e0' }}>
                  <TextField
                    size="small"
                    type="text"
                    value={formatDisplayValue(item.formatG?.tdItem ?? '')}
                    onChange={(e) => onDataChange(index, 'formatG', 'tdItem', e.target.value)}
                    onBlur={(e) => handleBlurToThreeDecimals(index, 'formatG', 'tdItem', e.target.value)}
                    sx={{
                      ...inputSx,
                      '& .MuiOutlinedInput-root': {
                        ...inputSx['& .MuiOutlinedInput-root'],
                        '& fieldset': {
                          borderColor: item.formatG?.tdItem && !hasValidFormat(item.formatG.tdItem) ? '#d32f2f' : undefined,
                        },
                      },
                    }}
                    inputProps={inputPropsDecimal}
                    error={item.formatG?.tdItem ? !hasValidFormat(item.formatG.tdItem) : false}
                  />
                </TableCell>
                {/* Format E columns */}
                <TableCell sx={{ border: '1px solid #e0e0e0' }}>
                  <TextField
                    size="small"
                    type="text"
                    value={formatDisplayValue(item.formatE?.tdKg ?? '')}
                    onChange={(e) => onDataChange(index, 'formatE', 'tdKg', e.target.value)}
                    onBlur={(e) => handleBlurToThreeDecimals(index, 'formatE', 'tdKg', e.target.value)}
                    sx={{
                      ...inputSx,
                      '& .MuiOutlinedInput-root': {
                        ...inputSx['& .MuiOutlinedInput-root'],
                        '& fieldset': {
                          borderColor: item.formatE?.tdKg && !hasValidFormat(item.formatE.tdKg) ? '#d32f2f' : undefined,
                        },
                      },
                    }}
                    inputProps={inputPropsDecimal}
                    error={item.formatE?.tdKg ? !hasValidFormat(item.formatE.tdKg) : false}
                  />
                </TableCell>
                <TableCell sx={{ border: '1px solid #e0e0e0' }}>
                  <TextField
                    size="small"
                    type="text"
                    value={formatDisplayValue(item.formatE?.tdItem ?? '')}
                    onChange={(e) => onDataChange(index, 'formatE', 'tdItem', e.target.value)}
                    onBlur={(e) => handleBlurToThreeDecimals(index, 'formatE', 'tdItem', e.target.value)}
                    sx={{
                      ...inputSx,
                      '& .MuiOutlinedInput-root': {
                        ...inputSx['& .MuiOutlinedInput-root'],
                        '& fieldset': {
                          borderColor: item.formatE?.tdItem && !hasValidFormat(item.formatE.tdItem) ? '#d32f2f' : undefined,
                        },
                      },
                    }}
                    inputProps={inputPropsDecimal}
                    error={item.formatE?.tdItem ? !hasValidFormat(item.formatE.tdItem) : false}
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}

function CRRTable({ data, onDataChange, onError, onSuccess, currentUserPpo, onCn61ExtraDataChange }: CRRTableProps): React.ReactElement {
  const [localData, setLocalData] = useState<CRRData>(data);
  const [uploadErrors, setUploadErrors] = useState<string[]>([]);
  const [inboundFileInputRef] = React.useState<React.RefObject<HTMLInputElement>>(React.createRef());
  const [outboundFileInputRef] = React.useState<React.RefObject<HTMLInputElement>>(React.createRef());
  // Store CN61 extra data from CSV upload
  const [cn61ExtraData, setCn61ExtraData] = useState<IpcCn61ExtraData[]>([]);

  // Update local data when prop data changes
  React.useEffect(() => {
    if (data) {
      setLocalData(data);
    }
  }, [data]);

  if (!data) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 4 }}>
        <Typography sx={{ color: '#666', fontStyle: 'italic' }}>
          No CRR data available
        </Typography>
      </Box>
    );
  }



  const { flow, dto, command } = localData;
  const hasInboundData = command.inbound !== null;
  const hasOutboundData = command.outbound !== null;

  if (!hasInboundData && !hasOutboundData) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 4 }}>
        <Typography sx={{ color: '#666', fontStyle: 'italic' }}>
          No CRR command data available
        </Typography>
      </Box>
    );
  }

  const handleDataChange = useCallback((
    direction: 'inbound' | 'outbound',
    priority: 'priority' | 'nonPriority',
    index: number,
    field: 'formatP' | 'formatG' | 'formatE',
    subField: 'tdKg' | 'tdItem',
    value: string
  ) => {
    setLocalData((prev) => {
      const newData = { ...prev };
      if (newData.command[direction] && newData.command[direction]![priority]) {
        const updatedArray = [...newData.command[direction]![priority]];
        const currentItem = updatedArray[index];
        
        // Initialize the format object if it doesn't exist
        if (!currentItem[field]) {
          currentItem[field] = { tdKg: null, tdItem: null };
        }
        
        // Store the value as entered by user (no automatic formatting)
        currentItem[field]![subField] = value === '' ? null : value;
        
        newData.command[direction]![priority] = updatedArray;
      }
      
      // Notify parent component of changes
      if (onDataChange) {
        onDataChange(newData);
      }
      
      return newData;
    });
  }, [onDataChange]);

  const handleBlur = useCallback((
    direction: 'inbound' | 'outbound',
    priority: 'priority' | 'nonPriority',
    index: number,
    field: 'formatP' | 'formatG' | 'formatE',
    subField: 'tdKg' | 'tdItem',
    value: string
  ) => {
    // Enforce up to 3 decimals; coerce to 3-decimal string if valid
    const sanitized = value.trim();
    const isValid = /^\d*(\.\d{0,3})?$/.test(sanitized);
    if (isValid && sanitized !== '') {
      const numericValue = parseFloat(sanitized);
      const formattedValue = isNaN(numericValue) ? '' : numericValue.toFixed(3);
      
      // Update local data with formatted value
      setLocalData((prev) => {
        const newData = { ...prev };
        if (newData.command[direction] && newData.command[direction]![priority]) {
          const updatedArray = [...newData.command[direction]![priority]];
          const currentItem = updatedArray[index];
          
          // Initialize the format object if it doesn't exist
          if (!currentItem[field]) {
            currentItem[field] = { tdKg: null, tdItem: null };
          }
          
          // Store the formatted value
          currentItem[field]![subField] = formattedValue;
          
          newData.command[direction]![priority] = updatedArray;
        }
        
        // Immediately notify parent on blur with final formatted value
        if (onDataChange) {
          onDataChange(newData);
        }
        
        return newData;
      });
    }
  }, [onDataChange]);

  // 3-decimal completeness validation
  const threeDecimalsRegex = useMemo(() => /^\d+\.\d{3}$/ , []);
  const isValueComplete = useCallback((val: unknown) => {
    if (val === null || val === undefined || val === '') return false;
    return threeDecimalsRegex.test(String(val));
  }, [threeDecimalsRegex]);

  const isDirectionComplete = useCallback((directionData: any | null) => {
    if (!directionData) return false;
    let complete = true;
    const checkArray = (arr: any[] | undefined | null) => {
      if (!arr) return;
      arr.forEach((item) => {
        ['formatP', 'formatG', 'formatE'].forEach((fmt) => {
          const f = item[fmt];
          if (!f) return;
          if (!isValueComplete(f.tdKg) || !isValueComplete(f.tdItem)) complete = false;
        });
      });
    };
    checkArray(directionData.priority);
    checkArray(directionData.nonPriority);
    return complete;
  }, [isValueComplete]);

  // CSV handling functions
  const handleCsvDownload = useCallback((direction: 'inbound' | 'outbound') => {
    try {
      // Block download if incomplete
      const target = direction === 'inbound' ? localData.command.inbound : localData.command.outbound;
      if (!isDirectionComplete(target)) {
        onError?.('Please fill all CRR TD rates with exactly 3 decimals before downloading');
        return;
      }
      // Create a filtered data structure for the specific direction
      const filteredData = {
        ...localData,
        command: {
          ...localData.command,
          inbound: direction === 'inbound' ? localData.command.inbound : null,
          outbound: direction === 'outbound' ? localData.command.outbound : null
        }
      };
      
      const csvContent = convertIpcCrrDataToCsv(filteredData, currentUserPpo);
      const filename = direction === 'inbound' 
        ? `td_rates_${currentUserPpo}_${localData.dto.ppo}_${localData.dto.year}.csv`
        : `td_rates_${localData.dto.ppo}_${currentUserPpo}_${localData.dto.year}.csv`;
      downloadIpcCrrCsv(csvContent, filename);
      onSuccess?.(`CSV file for ${direction} downloaded successfully`);
    } catch (error) {
      onError?.(`Error generating CSV file: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }, [localData, currentUserPpo, onSuccess, onError, isDirectionComplete]);

  const handleCsvUpload = useCallback(async (event: React.ChangeEvent<HTMLInputElement>, direction: 'inbound' | 'outbound') => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Clear previous errors
    setUploadErrors([]);

    try {
      const text = await file.text();
      
      // Parse CSV with year validation - also extracts CN61 extra data
      const { crrData: csvData, cn61ExtraData: extraData } = parseIpcCrrCsvWithCn61Extra(text, localData, currentUserPpo, localData.dto.year);
      
      // Filter CRR CSV data by direction
      const filteredCsvData = csvData.filter(row => row.direction === direction);
      
      // Filter CN61 extra data by direction
      const filteredCn61ExtraData = extraData.filter(row => row.direction === direction);
      
      if (filteredCsvData.length === 0 && filteredCn61ExtraData.length === 0) {
        const errorMsg = `CSV file contains no valid data rows for ${direction} direction`;
        setUploadErrors([errorMsg]);
        onError?.(errorMsg);
        return;
      }

      // Apply CRR data if present
      if (filteredCsvData.length > 0) {
        const updatedData = applyCsvDataToIpcCrr(filteredCsvData, localData);
        setLocalData(updatedData);
        onDataChange?.(updatedData);
      }
      
      // Store and notify about CN61 extra data if present
      if (filteredCn61ExtraData.length > 0) {
        // Merge with existing CN61 extra data (keep other direction's data)
        const otherDirectionData = cn61ExtraData.filter(row => row.direction !== direction);
        const mergedCn61Data = [...otherDirectionData, ...filteredCn61ExtraData];
        setCn61ExtraData(mergedCn61Data);
        onCn61ExtraDataChange?.(mergedCn61Data);
      }
      
      // Clear any previous errors on successful upload
      setUploadErrors([]);
      
      // Build success message
      let successMsg = '';
      if (filteredCsvData.length > 0 && filteredCn61ExtraData.length > 0) {
        successMsg = `CSV file for ${direction} uploaded successfully (including ${filteredCn61ExtraData.length / 2} CN61 extra fields)`;
      } else if (filteredCsvData.length > 0) {
        successMsg = `CSV file for ${direction} uploaded successfully`;
      } else {
        successMsg = `CSV file for ${direction} uploaded (${filteredCn61ExtraData.length / 2} CN61 extra fields only)`;
      }
      onSuccess?.(successMsg);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      
      // Split multiple errors if they were joined with |||
      const errorList = errorMessage.includes('|||') 
        ? errorMessage.split('|||') 
        : [`Error parsing CSV file: ${errorMessage}`];
      
      setUploadErrors(errorList);
      onError?.(errorList.join('\n'));
    } finally {
      // Reset file input
      event.target.value = '';
    }
  }, [localData, currentUserPpo, onDataChange, onError, onSuccess, cn61ExtraData, onCn61ExtraDataChange]);

  const handleSampleCsvDownload = useCallback(() => {
    try {
      const sampleCsv = generateIpcCrrSampleCsv(currentUserPpo, localData.dto.ppo, localData.dto.year);
      downloadIpcCrrCsv(sampleCsv, 'ipc_crr_sample.csv');
      onSuccess?.('Sample CSV downloaded successfully');
    } catch (error) {
      onError?.(`Error generating sample CSV: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }, [currentUserPpo, localData.dto.ppo, localData.dto.year, onSuccess, onError]);

  const renderDirectionTables = (direction: 'inbound' | 'outbound', directionLabel: string) => {
    if (!command[direction]) return null;

    const directionData = command[direction]!;
    const hasPriorityData = directionData.priority && directionData.priority.length > 0;
    const hasNonPriorityData = directionData.nonPriority && directionData.nonPriority.length > 0;

    if (!hasPriorityData && !hasNonPriorityData) return null;

    const fileInputRef = direction === 'inbound' ? inboundFileInputRef : outboundFileInputRef;

    return (
      <Box sx={{ mb: 4 }}>
        
        {hasPriorityData && (
          <CRRSubTable
            title={`${directionLabel} Priority`}
            data={directionData.priority}
            onDataChange={(index, field, subField, value) => handleDataChange(direction, 'priority', index, field, subField, value)}
            onBlur={(index, field, subField, value) => handleBlur(direction, 'priority', index, field, subField, value)}
          />
        )}
        
        {hasNonPriorityData && (
          <CRRSubTable
            title={`${directionLabel} Non-Priority`}
            data={directionData.nonPriority}
            onDataChange={(index, field, subField, value) => handleDataChange(direction, 'nonPriority', index, field, subField, value)}
            onBlur={(index, field, subField, value) => handleBlur(direction, 'nonPriority', index, field, subField, value)}
          />
        )}

        {/* Action buttons below the table */}
        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', mt: 2, mb: 3 }}>
          <Button
            variant="outlined"
            onClick={() => handleCsvDownload(direction)}
            sx={{
              borderColor: '#00ACE7',
              color: '#00ACE7',
              fontSize: '12px',
              fontWeight: 600,
              textTransform: 'none',
              padding: '6px 16px',
              '&:hover': {
                borderColor: '#0095C7',
                backgroundColor: '#E3F7FE',
              },
            }}
            data-testid={`ipc-crr-download-${direction}-csv-btn`}
          >
            Download TD Rates
          </Button>
          <Button
            variant="outlined"
            onClick={() => fileInputRef.current?.click()}
            sx={{
              borderColor: '#00ACE7',
              color: '#00ACE7',
              fontSize: '12px',
              fontWeight: 600,
              textTransform: 'none',
              padding: '6px 16px',
              '&:hover': {
                borderColor: '#0095C7',
                backgroundColor: '#E3F7FE',
              },
            }}
            data-testid={`ipc-crr-upload-${direction}-csv-btn`}
          >
            Upload TD Rates
          </Button>
        </Box>

        {/* Hidden file input for CSV upload */}
        <input
          ref={fileInputRef}
          type="file"
          accept=".csv"
          onChange={(e) => handleCsvUpload(e, direction)}
          style={{ display: 'none' }}
          data-testid={`ipc-crr-csv-file-input-${direction}`}
        />
      </Box>
    );
  };

  return (
    <Box sx={{ width: '100%', maxWidth: '900px' }}>
      {/* Report Header - matching CN61Table style */}
      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <Typography variant="h6" sx={{ fontWeight: 700, color: '#063142' }}>
            CRR Interconnect Configuration - {dto.ppo}
          </Typography>
        </Box>
        <Typography variant="body2" sx={{ color: '#666' }}>
          Year: {dto.year} | Direction: {dto.direction}
        </Typography>
      </Box>

      {/* Upload Errors Display */}
      {uploadErrors.length > 0 && (
        <Alert severity="error" sx={{ mb: 3 }} data-testid="ipc-crr-upload-errors">
          <AlertTitle>Upload Errors</AlertTitle>
          <List dense sx={{ pt: 0 }}>
            {uploadErrors.map((error, index) => (
              <ListItem key={index} sx={{ py: 0 }}>
                <ListItemText primary={error} />
              </ListItem>
            ))}
          </List>
        </Alert>
      )}

      {renderDirectionTables('inbound', 'Inbound')}
      {renderDirectionTables('outbound', 'Outbound')}
    </Box>
  );
}

export default CRRTable;

